import {
  Breakpoint,
  CardGroupCard,
  Color,
  ContentTable,
  ContentTableItem,
  HeadlineAndQty,
  ItemsCarousel,
  TableColumn,
  usePagination,
  UsePagination,
} from '@elearning-platform/ui';
import React, { useEffect, useMemo } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { KnowledgeBaseViewMode, LessonTableColumnName } from '../../../../enums';

import { CardsGroupContainer, ContentTableWrapper, StyledCard, Wrapper } from './categorySection.styled';
import { CategorySectionProps } from './categorySection.types';

export const CategorySection: React.FC<CategorySectionProps> = (props: CategorySectionProps): JSX.Element => {
  const {
    isLoading,
    tableData,
    cardData,
    listLength,
    categoryTitle,
    withVerticalAlignment,
    emptyGridColors,
    viewMode,
  }: CategorySectionProps = props;
  const { pagesCount, page, setMaxItems, setPage, itemsPerPage }: UsePagination = usePagination(4);
  const isTablet: boolean = useMediaQuery({
    query: Breakpoint.Tablet,
  });
  const { t }: TransProps<never> = useTranslation();
  const isListMode: boolean = viewMode === KnowledgeBaseViewMode.List;

  useEffect((): void => {
    setMaxItems(listLength);
  }, [listLength, setMaxItems]);

  const lessonsTableColumns: TableColumn[] = [
    { name: LessonTableColumnName.Image, label: t('competence.app.knowledgeBaseV2.tableColumnLabel.material') },
    { name: LessonTableColumnName.Title, label: '' },
    { name: LessonTableColumnName.Type, label: t('competence.app.knowledgeBaseV2.tableColumnLabel.type') },
    { name: LessonTableColumnName.Status, label: t('competence.app.knowledgeBaseV2.tableColumnLabel.status') },
    { name: LessonTableColumnName.Actions, label: '' },
  ];

  const currentPageData: ContentTableItem[] = useMemo((): ContentTableItem[] => {
    const start: number = (page - 1) * itemsPerPage;

    return tableData.slice(start, start + itemsPerPage);
  }, [tableData, page, itemsPerPage]);

  return (
    <Wrapper>
      {(!isListMode || !isTablet) ? (
        <CardsGroupContainer>
          <ItemsCarousel
            emptyPlaceholderData={{
              text: t('competence.app.knowledgeBaseV2.categorySection.emptyListMessage'),
              colorsData: emptyGridColors,
            }}
            headlineData={{
              headline: categoryTitle,
            }}
            items={cardData ?? []}
            renderItem={(item: CardGroupCard): JSX.Element => (
              <StyledCard
                {...item}
                isLoading={isLoading}
                tagsData={{
                  list: item.tags,
                  colorsData: {
                    backgroundColor: Color.White,
                    borderColor: Color.Gray300,
                    textColor: Color.Gray600,
                  },
                }}
              />
            )}
            scrollUpText={(withVerticalAlignment || isListMode)
              ? t('competence.app.knowledgeBaseV2.categorySection.toTop')
              : undefined
            }
            showMoreText={(withVerticalAlignment || isListMode)
              ? t('competence.app.knowledgeBaseV2.categorySection.showMore')
              : undefined
            }
          />
        </CardsGroupContainer>
      ) : (
        <ContentTableWrapper>
          <HeadlineAndQty headline={categoryTitle} quantity={listLength} />
          <ContentTable
            columns={lessonsTableColumns}
            isLoading={isLoading}
            items={currentPageData}
            paginationSettings={{ activePage: page, pagesCount, onActivePageChange: setPage }}
          />
        </ContentTableWrapper>
      )}
    </Wrapper>
  );
};
